// src/components/ReservatioModal.jsx

import React, { useState, useEffect } from "react"; // 1. Import React and necessary hooks
import { Modal, Button, Form, Spinner, Alert } from "react-bootstrap"; // 2. Import React Bootstrap components for modal and form elements
import axiosInstance from "../api/axiosInstance"; // 3. Import custom Axios instance for API calls
import { useAuth } from "../context/AuthContext"; // 4. Import custom authentication hook
import DatePicker from "react-datepicker"; // 5. Import DatePicker component for date selection
import "react-datepicker/dist/react-datepicker.css"; // 6. Import DatePicker styles
import "./ReservatioModal.css"; // 7. Import custom CSS for ReservatioModal

/**
 * 8. ReservatioModal Component
 * This component renders a modal dialog for creating a new booking.
 * It includes guest search functionality, booking details, room selection, and payment information.
 *
 * Props:
 * - show (boolean): Controls the visibility of the modal.
 * - handleClose (function): Function to close the modal.
 * - onReservationSuccess (function): Callback function invoked after a successful booking.
 */
const ReservatioModal = ({ show, handleClose, onReservationSuccess }) => {
  const { authData, logout } = useAuth(); // 9. Destructure authentication data and logout function from context

  // 10. State to manage booking form data
  const [formData, setFormData] = useState({
    idNumber: "",
    firstName: "",
    lastName: "",
    email: "",
    mobileNumber: "",
    phoneNumber: "",
    address: "",
    dateOfBirth: null,
    checkInDate: null,
    checkOutDate: null,
    roomId: "",
    roomTypeId: "",
    totalAmount: "",
    paidAmount: "",
    discount: "",
    remarks: "",
    notes: "",
    numberOfAdults: 1,
    numberOfKids: 0,
    includesKids: false,
    guestId: null,
  });

  // 11. Additional states for managing rooms, loading indicators, errors, and messages
  const [availableRooms, setAvailableRooms] = useState([]); // 12. List of available rooms based on selected dates
  const [filteredRooms, setFilteredRooms] = useState([]); // 13. Rooms filtered by selected room type
  const [roomTypes, setRoomTypes] = useState([]); // 14. List of room types fetched from the server
  const [selectedRoomType, setSelectedRoomType] = useState(""); // 15. Currently selected room type for filtering
  const [isLoadingRooms, setIsLoadingRooms] = useState(false); // 16. Loading state for fetching available rooms
  const [selectedRoom, setSelectedRoom] = useState(null); // 17. Currently selected room
  const [isLoadingRoomTypes, setIsLoadingRoomTypes] = useState(false); // 18. Loading state for fetching room types
  const [isSearching, setIsSearching] = useState(false); // 19. Loading state for guest search
  const [guestFound, setGuestFound] = useState(null); // 20. Indicates if a guest was found based on ID search
  const [error, setError] = useState(null); // 21. Error message to display to the user
  const [successMessage, setSuccessMessage] = useState(""); // 22. Success message to display to the user
  const [searchIdNumber, setSearchIdNumber] = useState(""); // 23. ID number entered for guest search

  /**
   * 24. useEffect hook to fetch room types when the hotelId changes or when the modal is shown.
   * This ensures that the room types are always up-to-date based on the authenticated hotel's ID.
   */
  useEffect(() => {
    const fetchRoomTypes = async () => {
      try {
        setIsLoadingRoomTypes(true); // 25. Start loading indicator for room types
        const response = await axiosInstance.get(
          `/RoomTypes/GetRoomTypes/${authData?.hotelId}`
        );
        //console.log("Room Types API Response:", response.data); // 26. Log the response for debugging

        if (Array.isArray(response.data)) {
          setRoomTypes(Array.isArray(response.data) ? response.data : []); // 27. Update roomTypes state with fetched data
        } else {
          throw new Error("Unexpected response structure for room types."); // 28. Throw error if response format is unexpected
        }
      } catch (err) {
        console.error("Error fetching room types:", err); // 29. Log the error for debugging
        setError("Failed to fetch room types. Please try again."); // 30. Set error message for the user
      } finally {
        setIsLoadingRoomTypes(false); // 31. Stop loading indicator
      }
    };

    if (authData?.hotelId && show) {
      fetchRoomTypes(); // 32. Fetch room types only if hotelId is available and modal is shown
    }
  }, [authData?.hotelId, show]); // 33. Dependency array ensures this runs when hotelId or show changes

  /**
   * 34. Function to fetch available rooms based on selected check-in and check-out dates.
   * It sends a GET request to the server with necessary parameters.
   */
  const fetchAvailableRooms = async () => {
    // 35. Do not proceed if either check-in or check-out date is not selected
    if (!formData.checkInDate || !formData.checkOutDate) return;

    try {
      setIsLoadingRooms(true); // 36. Start loading indicator for rooms
      const response = await axiosInstance.get("/Rooms/AvailableRooms", {
        params: {
          hotelId: authData?.hotelId,
          checkInDate: formData.checkInDate.toISOString(),
          checkOutDate: formData.checkOutDate.toISOString(),
        },
      });

      //console.log("Available Rooms API Response:", response.data); // 37. Log the response for debugging

      setAvailableRooms(Array.isArray(response.data) ? response.data : []); // 38. Update availableRooms state with fetched data
      setFilteredRooms(Array.isArray(response.data) ? response.data : []); // 39. Initially, all available rooms are shown
    } catch (err) {
      console.error("Error fetching rooms:", err); // 40. Log the error for debugging
      setError("Failed to fetch available rooms. Please try again."); // 41. Set error message for the user
    } finally {
      setIsLoadingRooms(false); // 42. Stop loading indicator
    }
  };

  /**
   * 43. Handler for input changes in the booking form.
   * It updates the formData state based on user input.
   */
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target; // 44. Destructure necessary properties from the event
    const inputValue = type === "checkbox" ? checked : value; // 45. Determine the value based on input type
    setFormData((prev) => ({ ...prev, [name]: inputValue })); // 46. Update formData state
  };

  /**
   * 47. Handler for date changes using the DatePicker component.
   * It updates the corresponding date field in formData.
   */
  const handleDateChange = (date, name) => {
    setFormData((prev) => ({ ...prev, [name]: date })); // 48. Update the specific date field
  };

  /**
   * 49. Handler for selecting or unselecting a room.
   * It toggles the selected room and updates the formData accordingly.
   */
  const handleRoomSelection = (room) => {
    if (selectedRoom && selectedRoom.roomId === room.roomId) {
      // 50. If the clicked room is already selected, unselect it
      setSelectedRoom(null); // 51. Reset selectedRoom state
      setFormData((prev) => ({
        ...prev,
        roomId: "",
        roomTypeId: "",
        totalAmount: "",
      })); // 52. Clear room-related fields in formData
      setSuccessMessage(""); // 53. Clear any success messages
    } else {
      // 54. Select the new room
      const roomTypeId = room.roomType?.roomTypeId || room.roomTypeId; // 55. Get room type ID

      //console.log(`RoomTypeId for Room ${room.roomNumber}: ${roomTypeId}`); // 56. Log room type ID for debugging

      setSelectedRoom(room); // 57. Update selectedRoom state
      setFormData((prev) => ({
        ...prev,
        roomId: room.roomId,
        roomTypeId: roomTypeId,
        totalAmount: room.price, // 58. Set the total amount based on room price
      })); // 59. Update formData with selected room details
      setSuccessMessage(
        `Selected Room: ${room.roomNumber}, ${room.roomType?.typeName || "N/A"}`
      ); // 60. Set success message for the user
    }
  };

  /**
   * 61. Handler for filtering rooms based on selected room type.
   * It updates the filteredRooms state to show only rooms matching the selected type.
   */
  const handleRoomTypeFilter = (e) => {
    const selectedTypeId = e.target.value; // 62. Get the selected room type ID
    setSelectedRoomType(selectedTypeId); // 63. Update selectedRoomType state

    if (selectedTypeId === "") {
      setFilteredRooms(availableRooms); // 64. If no filter is selected, show all available rooms
    } else {
      // 65. Filter rooms based on the selected room type ID
      const filtered = availableRooms.filter(
        (room) => room.roomTypeId.toString() === selectedTypeId
      );
      setFilteredRooms(filtered); // 66. Update filteredRooms state with the filtered list
    }

    // 67. Optionally, reset selected room if it doesn't match the new filter
    if (
      selectedRoom &&
      selectedRoom.roomTypeId.toString() !== selectedTypeId
    ) {
      setSelectedRoom(null); // 68. Unselect the current room
      setFormData((prev) => ({
        ...prev,
        roomId: "",
        roomTypeId: "",
        totalAmount: "",
      })); // 69. Clear room-related fields in formData
      setSuccessMessage(""); // 70. Clear any success messages
    }
  };

  /**
   * 71. Handler for form submission to create a new booking.
   * It validates the form data and sends a POST request to the server.
   */
  const handleBookingSubmit = async (e) => {
    e.preventDefault(); // 72. Prevent the default form submission behavior
    e.stopPropagation(); // 73. Stop the event from propagating further
    //console.log("Form submitted! Default action prevented."); // 74. Log form submission for debugging

    // 75. List of required fields that must be filled out
    const requiredFields = [
      "idNumber",
      "firstName",
      "lastName",
      "email",
      "address",
      "checkInDate",
      "checkOutDate",
      "roomId",
    ];

    // 76. Iterate through required fields to ensure they are not empty
    for (const field of requiredFields) {
      if (
        formData[field] === null ||
        formData[field] === undefined ||
        formData[field].toString().trim() === ""
      ) {
        // 77. Format the field name for display (e.g., "idNumber" to "Id Number")
        const formattedField = field
          .replace(/([A-Z])/g, " $1") // 78. Insert space before uppercase letters
          .replace(/^./, (str) => str.toUpperCase()); // 79. Capitalize the first letter
        setError(`${formattedField} is required.`); // 80. Set the error message
        return; // 81. Exit the function early due to validation failure
      }
    }

    // 82. Validate that at least one contact number is provided (mobile or phone)
    if (
      (!formData.mobileNumber || formData.mobileNumber.trim() === "") &&
      (!formData.phoneNumber || formData.phoneNumber.trim() === "")
    ) {
      setError("Either Mobile Number or Phone Number is required."); // 83. Set the error message
      return; // 84. Exit the function early due to validation failure
    }

    // 85. Additional validations can be added here (e.g., email format, phone number format)

    // 86. Debugging: Log formData before sending it to the server
    //console.log("Form Data Before Submission:", formData);

    try {
      setSuccessMessage(""); // 87. Clear any existing success messages
      setError(null); // 88. Clear any existing error messages

      // 89. Additional validation to ensure idNumber is not empty or null
      if (!formData.idNumber || formData.idNumber.trim() === "") {
        setError("ID/Passport Number is required."); // 90. Set the error message
        return; // 91. Exit the function early due to validation failure
      }

      // 92. Prepare the payload to send to the server
      const payload = {
        idNumber: formData.idNumber,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        mobileNumber: formData.mobileNumber.trim(),
        phoneNumber: formData.phoneNumber.trim(),
        address: formData.address,
        dateOfBirth: formData.dateOfBirth
          ? formData.dateOfBirth.toISOString()
          : null,
        roomTypeId: formData.roomTypeId
          ? parseInt(formData.roomTypeId, 10)
          : null,
        checkInDate: formData.checkInDate.toISOString(),
        checkOutDate: formData.checkOutDate.toISOString(),
        roomId: formData.roomId,
        totalAmount: parseFloat(formData.totalAmount) || 0,
        paidAmount: parseFloat(formData.paidAmount) || 0,
        discount: parseFloat(formData.discount) || 0,
        remarks: formData.remarks || "",
        notes: formData.notes || "",
        hotelId: authData.hotelId, // 93. Associate the booking with the authenticated hotel
        numberOfAdults: parseInt(formData.numberOfAdults, 10) || 1,
        numberOfKids: parseInt(formData.numberOfKids, 10) || 0,
        includesKids: formData.includesKids,
        guestId: formData.guestId, // 94. This will be null if guest doesn't exist
      };

      // 95. Debugging: Log the payload before sending it to the server
      //console.log("Payload Being Sent:", payload);

      // 96. Send a POST request to create a new booking
      const response = await axiosInstance.post("/Bookings", payload);
      //console.log("Booking Response:", response.data); // 97. Log the server's response
      setSuccessMessage("Booking successful!"); // 98. Set success message for the user
      resetForm(); // 99. Reset the form fields after successful booking
      handleClose(); // 100. Close the modal after successful booking

      // 101. Invoke the callback to notify parent component of successful booking
      if (onReservationSuccess) {
        onReservationSuccess(); // 102. Call the success callback if provided
      }
    } catch (err) {
      console.error("Error creating booking:", err); // 103. Log the error for debugging
      if (err.response && err.response.status === 401) {
        // 104. If the server responds with a 401 Unauthorized status
        setError("Session expired. Please log in again."); // 105. Set the error message
        // 106. Trigger logout
        logout();
        // 107. Optionally, you can navigate to the login page or refresh the page
      } else if (err.response && err.response.data) {
        // 108. If the backend sends a detailed error message
        setError(
          err.response.data.message ||
            "Failed to create booking. Please try again."
        ); // 109. Set the error message based on the server's response
      } else {
        setError("Failed to create booking. Please try again."); // 110. Set a generic error message
      }
    }
  };

  /**
   * 111. Function to reset the booking form after a successful booking.
   * It clears all form fields and related states.
   */
  const resetForm = () => {
    setFormData({
      idNumber: "",
      firstName: "",
      lastName: "",
      email: "",
      mobileNumber: "",
      phoneNumber: "",
      address: "",
      dateOfBirth: null,
      checkInDate: null,
      checkOutDate: null,
      roomId: "",
      roomTypeId: "",
      totalAmount: "",
      paidAmount: "",
      discount: "",
      remarks: "",
      notes: "",
      numberOfAdults: 1,
      numberOfKids: 0,
      includesKids: false,
      guestId: null,
    }); // 112. Reset all form fields to their initial state
    setSelectedRoom(null); // 113. Unselect any selected room
    setAvailableRooms([]); // 114. Clear the list of available rooms
    setFilteredRooms([]); // 115. Clear the list of filtered rooms
    setSelectedRoomType(""); // 116. Reset the room type filter
    setGuestFound(null); // 117. Reset the guest search state
    setSearchIdNumber(""); // 118. Clear the guest search ID number
  };

  /**
   * 119. useEffect hook to fetch available rooms whenever the check-in or check-out dates change.
   * This ensures that the list of available rooms is always up-to-date based on the selected dates.
   */
  useEffect(() => {
    if (formData.checkInDate && formData.checkOutDate) {
      fetchAvailableRooms(); // 120. Fetch available rooms if both dates are selected
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.checkInDate, formData.checkOutDate]); // 121. Dependency array ensures this runs when either date changes

  /**
   * 122. useEffect hook for debugging purposes.
   * It logs the authentication data to verify the presence of token and hotelId.
   */
  useEffect(() => {
    //console.log("Auth Data:", authData); // 123. Log authData for debugging
  }, [authData]); // 124. Runs whenever authData changes

  /**
   * 125. Handler for searching a guest based on the entered ID number.
   * It sends a GET request to the server to fetch guest details.
   */
  const handleGuestSearch = async (e) => {
    e.preventDefault(); // 126. Prevent the default form submission behavior
    if (!searchIdNumber.trim()) {
      setError("Please enter a valid ID Number to search."); // 127. Set error if search ID is empty
      return; // 128. Exit the function early due to validation failure
    }

    try {
      setIsSearching(true); // 129. Start loading indicator for guest search
      setError(null); // 130. Clear any existing error messages
      setSuccessMessage(""); // 131. Clear any existing success messages

      // 132. Send a GET request to search for the guest by ID number and hotel ID
      const response = await axiosInstance.get(
        "/Guests/SearchByIdNumberAndHotel",
        {
          params: {
            idNumber: searchIdNumber.trim(),
            hotelId: authData.hotelId,
          },
        }
      );

      const guest = response.data; // 133. Extract guest data from the response
      //console.log("Guest found:", guest); // 134. Log the guest data for debugging

      if (guest) {
        // 135. If a guest is found
        setGuestFound(true); // 136. Indicate that the guest was found
        setFormData((prev) => ({
          ...prev,
          idNumber: guest.idNumber || "",
          firstName: guest.firstName || "",
          lastName: guest.lastName || "",
          email: guest.email || "",
          mobileNumber: guest.mobileNumber || "",
          phoneNumber: guest.phoneNumber || "",
          address: guest.address || "",
          dateOfBirth: guest.dateOfBirth ? new Date(guest.dateOfBirth) : null,
          guestId: guest.guestId || null,
        })); // 137. Populate form fields with guest data
        setSuccessMessage(
          "Guest found. You can reuse the details or edit them."
        ); // 138. Set success message for the user
      } else {
        // 139. If no guest is found
        setGuestFound(false); // 140. Indicate that the guest was not found
        setFormData((prev) => ({
          ...prev,
          idNumber: searchIdNumber.trim(),
          firstName: "",
          lastName: "",
          email: "",
          mobileNumber: "",
          phoneNumber: "",
          address: "",
          dateOfBirth: null,
          guestId: null,
        })); // 141. Clear guest-related form fields
        setSuccessMessage(
          "Guest not found. Please enter guest details manually."
        ); // 142. Set informational message for the user
      }
    } catch (err) {
      console.error("Error searching for guest:", err); // 143. Log the error for debugging
      if (err.response && err.response.status === 404) {
        // 144. If the server responds with a 404 Not Found status
        setGuestFound(false); // 145. Indicate that the guest was not found
        setFormData((prev) => ({
          ...prev,
          idNumber: searchIdNumber.trim(),
          firstName: "",
          lastName: "",
          email: "",
          mobileNumber: "",
          phoneNumber: "",
          address: "",
          dateOfBirth: null,
          guestId: null,
        })); // 146. Clear guest-related form fields
        setSuccessMessage(
          "Guest not found. Please enter guest details manually."
        ); // 147. Set informational message for the user
      } else {
        setError("An error occurred while searching for the guest."); // 148. Set a generic error message
      }
    } finally {
      setIsSearching(false); // 149. Stop loading indicator
    }
  };

  return (
    <Modal
      show={show} // 150. Control the visibility of the modal
      onHide={handleClose} // 151. Function to call when the modal is requested to be closed
      size="lg" // 152. Set the size of the modal to large
      backdrop="static" // 153. Prevent closing the modal by clicking on the backdrop
      keyboard={false} // 154. Prevent closing the modal with the keyboard (e.g., Esc key)
      aria-labelledby="reservatio-modal-title" // 155. Accessibility attribute linking to the modal title
      centered // 156. Center the modal vertically in the viewport
    >
      {/* 157. Modal Header with Close Button */}
      <Modal.Header closeButton>
        <Modal.Title id="reservatio-modal-title">Create a Reservation</Modal.Title> {/* 158. Modal Title */}
      </Modal.Header>

      {/* 159. Modal Body containing the forms and messages */}
      <Modal.Body>
        {/* 160. Display error message if any */}
        {error && <Alert variant="danger">{error}</Alert>}

        {/* 161. Display success message if any */}
        {successMessage && <Alert variant="success">{successMessage}</Alert>}

        {/* 162. Guest Search Section */}
        <Form className="guest-search-form" onSubmit={handleGuestSearch}>
          <fieldset> {/* 163. Group related fields */}
            <legend>Search Guest</legend> {/* 164. Title for the fieldset */}
            <Form.Group controlId="searchIdNumber" className="mb-3"> {/* 165. Form group for ID/Passport Number */}
              <Form.Label>ID/Passport Number *</Form.Label> {/* 166. Label for input */}
              <Form.Control
                type="text"
                name="searchIdNumber"
                value={searchIdNumber}
                onChange={(e) => setSearchIdNumber(e.target.value)}
                required
                placeholder="Enter ID/Passport Number" // 167. Placeholder text
              />
            </Form.Group>
            <Button type="submit" disabled={isSearching}>
              {/* 168. Submit button with disabled state */}
              {isSearching ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />{" "}
                  Searching... {/* 169. Show spinner and "Searching..." text when searching */}
                </>
              ) : (
                "Search Guest" // 170. Show "Search Guest" otherwise
              )}
            </Button>
          </fieldset>
        </Form>

        {/* 171. Booking Form - Conditionally Rendered Based on Guest Search Result */}
        {guestFound !== null && (
          <Form className="booking-form mt-4" onSubmit={handleBookingSubmit}>
            {/* 172. Guest Details Section */}
            <fieldset>
              <legend>Guest Details</legend> {/* 173. Title for the fieldset */}
              <div className="row"> {/* 174. Row layout for form fields */}
                {/* 175. ID/Passport Number Field */}
                <Form.Group as="div" className="mb-3 col-md-6">
                  <Form.Label>ID/Passport Number *</Form.Label>
                  <Form.Control
                    type="text"
                    name="idNumber"
                    value={formData.idNumber}
                    onChange={handleInputChange}
                    required
                    disabled={guestFound} // 176. Disable the field if guest was found to prevent changing IDNumber
                  />
                </Form.Group>

                {/* 177. First Name Field */}
                <Form.Group as="div" className="mb-3 col-md-6">
                  <Form.Label>First Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>

                {/* 178. Last Name Field */}
                <Form.Group as="div" className="mb-3 col-md-6">
                  <Form.Label>Last Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>

                {/* 179. Email Field */}
                <Form.Group as="div" className="mb-3 col-md-6">
                  <Form.Label>Email *</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>

                {/* 180. Mobile Number Field */}
                <Form.Group as="div" className="mb-3 col-md-6">
                  <Form.Label>Mobile Number</Form.Label>
                  <Form.Control
                    type="text"
                    name="mobileNumber"
                    value={formData.mobileNumber}
                    onChange={handleInputChange}
                  />
                </Form.Group>

                {/* 181. Phone Number Field */}
                <Form.Group as="div" className="mb-3 col-md-6">
                  <Form.Label>Phone Number</Form.Label>
                  <Form.Control
                    type="text"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                  />
                </Form.Group>

                {/* 182. Address Field */}
                <Form.Group as="div" className="mb-3 col-md-12">
                  <Form.Label>Address *</Form.Label>
                  <Form.Control
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>

                {/* 183. Date of Birth Field using DatePicker */}
                <Form.Group as="div" className="mb-3 col-md-6">
                  <Form.Label>Date of Birth</Form.Label>
                  <DatePicker
                    selected={formData.dateOfBirth}
                    onChange={(date) => handleDateChange(date, "dateOfBirth")}
                    dateFormat="yyyy-MM-dd" // 184. Date format
                    maxDate={new Date()} // 185. Prevent selecting future dates
                    isClearable // 186. Allow clearing the date selection
                    className="form-control" // 187. Apply Bootstrap form-control class
                    placeholderText="Select Date of Birth" // 188. Placeholder text
                  />
                </Form.Group>
              </div>
            </fieldset>

            {/* 189. Number of Guests Section */}
            <fieldset>
              <legend>Number of Guests</legend> {/* 190. Title for the fieldset */}
              <div className="row"> {/* 191. Row layout for form fields */}
                {/* 192. Number of Adults Field */}
                <Form.Group as="div" className="mb-3 col-md-4">
                  <Form.Label>Adults *</Form.Label>
                  <Form.Control
                    type="number"
                    name="numberOfAdults"
                    value={formData.numberOfAdults}
                    onChange={handleInputChange}
                    min="1" // 193. Minimum of 1 adult
                    required
                  />
                </Form.Group>

                {/* 194. Checkbox to indicate if there are kids */}
                <Form.Group as="div" className="mb-3 col-md-4 d-flex align-items-center">
                  <Form.Check
                    type="checkbox"
                    name="includesKids"
                    id="includesKids"
                    label="Are there kids?"
                    checked={formData.includesKids}
                    onChange={handleInputChange}
                  />
                </Form.Group>

                {/* 195. Number of Kids Field - Conditionally Rendered */}
                {formData.includesKids && (
                  <Form.Group as="div" className="mb-3 col-md-4">
                    <Form.Label>Number of Kids</Form.Label>
                    <Form.Control
                      type="number"
                      name="numberOfKids"
                      value={formData.numberOfKids}
                      onChange={handleInputChange}
                      min="0" // 196. Minimum of 0 kids
                    />
                  </Form.Group>
                )}
              </div>
            </fieldset>

            {/* 197. Booking Dates Section */}
            <fieldset>
              <legend>Booking Dates</legend> {/* 198. Title for the fieldset */}
              <div className="row"> {/* 199. Row layout for form fields */}
                {/* 200. Check-In Date Field using DatePicker */}
                <Form.Group as="div" className="mb-3 col-md-6">
                  <Form.Label>Check-In Date *</Form.Label>
                  <DatePicker
                    selected={formData.checkInDate}
                    onChange={(date) => handleDateChange(date, "checkInDate")}
                    dateFormat="yyyy-MM-dd" // 201. Date format
                    minDate={new Date()} // 202. Prevent selecting past dates
                    required
                    className="form-control" // 203. Apply Bootstrap form-control class
                  />
                </Form.Group>

                {/* 204. Check-Out Date Field using DatePicker */}
                <Form.Group as="div" className="mb-3 col-md-6">
                  <Form.Label>Check-Out Date *</Form.Label>
                  <DatePicker
                    selected={formData.checkOutDate}
                    onChange={(date) => handleDateChange(date, "checkOutDate")}
                    dateFormat="yyyy-MM-dd" // 205. Date format
                    minDate={
                      formData.checkInDate
                        ? new Date(
                            formData.checkInDate.getTime() + 24 * 60 * 60 * 1000
                          )
                        : new Date()
                    } // 206. Set the minimum check-out date to one day after check-in date
                    required
                    className="form-control" // 207. Apply Bootstrap form-control class
                  />
                </Form.Group>
              </div>
            </fieldset>

            {/* 208. Available Rooms Section */}
            <fieldset>
              <legend>Available Rooms</legend> {/* 209. Title for the fieldset */}
              <Form.Group as="div" className="mb-3">
                <Form.Label>Filter by Room Type:</Form.Label> {/* 210. Label for room type filter */}
                {isLoadingRoomTypes ? (
                  // 211. Display a loading spinner while room types are being fetched
                  <div>
                    <Spinner animation="border" size="sm" role="status" aria-hidden="true" />{" "}
                    Loading room types...
                  </div>
                ) : roomTypes.length > 0 ? (
                  // 212. Dropdown to select a room type for filtering
                  <Form.Select
                    value={selectedRoomType}
                    onChange={handleRoomTypeFilter}
                  >
                    <option value="">All Room Types</option> {/* 213. Option to show all room types */}
                    {roomTypes.map((type) => (
                      <option key={type.roomTypeId} value={type.roomTypeId}>
                        {type.typeName}
                      </option> // 214. Populate dropdown with room types
                    ))}
                  </Form.Select>
                ) : (
                  // 215. Inform the user if no room types are available
                  <div>No room types available.</div>
                )}
              </Form.Group>

              <div className="rooms-container"> {/* 216. Container for displaying rooms */}
                {isLoadingRooms ? (
                  // 217. Display a loading spinner while available rooms are being fetched
                  <div>
                    <Spinner animation="border" size="sm" role="status" aria-hidden="true" />{" "}
                    Loading available rooms...
                  </div>
                ) : selectedRoom ? (
                  // 218. If a room is selected, display only that room with an option to unselect
                  <div className="room-card">
                    <p>Room Number: {selectedRoom.roomNumber}</p>
                    <p>Room Type: {selectedRoom.roomType?.typeName || "N/A"}</p>
                    <p>
                      Room Type ID: {selectedRoom.roomType?.roomTypeId || "N/A"}
                    </p>
                    <p>Price: ${selectedRoom.price}</p>
                    <p>Capacity: {selectedRoom.capacity || "N/A"}</p>
                    <p>
                      Availability:{" "}
                      {selectedRoom.nextAvailableDate
                        ? new Date(selectedRoom.nextAvailableDate).toLocaleDateString()
                        : "Available Now"}
                    </p>
                    <Button
                      variant="danger"
                      onClick={() => handleRoomSelection(selectedRoom)}
                    >
                      Unselect Room {/* 219. Button to unselect the selected room */}
                    </Button>
                  </div>
                ) : filteredRooms.length > 0 ? (
                  // 220. If no room is selected, display all filtered available rooms
                  filteredRooms.map((room) => (
                    <div className="room-card" key={room.roomId}>
                      <p>Room Number: {room.roomNumber}</p>
                      <p>Room Type: {room.roomType?.typeName || "N/A"}</p>
                      <p>
                        Room Type ID: {room.roomType?.roomTypeId || "N/A"}
                      </p>
                      <p>Price: ${room.price}</p>
                      <p>Capacity: {room.capacity || "N/A"}</p>
                      <p>
                        Availability:{" "}
                        {room.nextAvailableDate
                          ? new Date(room.nextAvailableDate).toLocaleDateString()
                          : "Available Now"}
                      </p>
                      <Button
                        variant={
                          selectedRoom && selectedRoom.roomId === room.roomId
                            ? "secondary"
                            : "primary"
                        }
                        onClick={() => handleRoomSelection(room)}
                        disabled={selectedRoom && selectedRoom.roomId !== room.roomId} // 221. Disable button if another room is selected
                      >
                        {selectedRoom && selectedRoom.roomId === room.roomId
                          ? "Selected"
                          : "Select Room"} {/* 222. Dynamic button text based on selection */}
                      </Button>
                    </div>
                  ))
                ) : (
                  // 223. Inform the user if no rooms are available for the selected dates
                  <div>No rooms available for the selected dates.</div>
                )}
              </div>
            </fieldset>

            {/* 224. Payment Details Section */}
            <fieldset>
              <legend>Payment Details</legend> {/* 225. Title for the fieldset */}
              <div className="row"> {/* 226. Row layout for form fields */}
                {/* 227. Total Amount Field */}
                <Form.Group as="div" className="mb-3 col-md-4">
                  <Form.Label>Total Amount *</Form.Label>
                  <Form.Control
                    type="number"
                    name="totalAmount"
                    value={formData.totalAmount}
                    onChange={handleInputChange}
                    min="0" // 228. Minimum value of 0
                    required
                  />
                </Form.Group>

                {/* 229. Paid Amount Field */}
                <Form.Group as="div" className="mb-3 col-md-4">
                  <Form.Label>Paid Amount</Form.Label>
                  <Form.Control
                    type="number"
                    name="paidAmount"
                    value={formData.paidAmount}
                    onChange={handleInputChange}
                    min="0" // 230. Minimum value of 0
                  />
                </Form.Group>

                {/* 231. Discount Field */}
                <Form.Group as="div" className="mb-3 col-md-4">
                  <Form.Label>Discount</Form.Label>
                  <Form.Control
                    type="number"
                    name="discount"
                    value={formData.discount}
                    onChange={handleInputChange}
                    min="0" // 232. Minimum value of 0
                  />
                </Form.Group>

                {/* 233. Remarks Field */}
                <Form.Group as="div" className="mb-3 col-md-6">
                  <Form.Label>Remarks</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    name="remarks"
                    value={formData.remarks}
                    onChange={handleInputChange}
                  />
                </Form.Group>

                {/* 234. Notes Field */}
                <Form.Group as="div" className="mb-3 col-md-6">
                  <Form.Label>Notes</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </div>
            </fieldset>

            {/* 235. Confirm Booking Button */}
            <div className="d-flex justify-content-end"> {/* 236. Container to align the button to the end */}
              <Button
                variant="success"
                type="submit"
                disabled={isLoadingRooms || isLoadingRoomTypes} // 237. Disable the button while loading
              >
                {isLoadingRooms || isLoadingRoomTypes ? (
                  // 238. Show spinner and "Processing..." text when loading
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />{" "}
                    Processing...
                  </>
                ) : (
                  "Confirm Booking" // 239. Show "Confirm Booking" otherwise
                )}
              </Button>
            </div>
          </Form>
        )}
      </Modal.Body>

      {/* 240. Modal Footer with Close Button */}
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Close {/* 241. Button to close the modal */}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ReservatioModal; // 242. Export the ReservatioModal component
